import React, { useState, useEffect } from 'react';
import { Building2, BedDouble, Plus, Users, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

function HebergementPage() {
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuildingDialog, setShowBuildingDialog] = useState(false);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [showBedDialog, setShowBedDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [newBuilding, setNewBuilding] = useState({ name: '', description: '', gender: 'male', floors: 2 });
  const [newRoom, setNewRoom] = useState({ name: '', building_id: '', floor: 0, room_type: 'dortoir', capacity: 10 });
  const [newBed, setNewBed] = useState({ room_id: '', bed_number: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [b, r, be, s] = await Promise.all([
        api.get('/buildings'), api.get('/rooms'), api.get('/beds'), api.get('/students')
      ]);
      setBuildings(b.data);
      setRooms(r.data);
      setBeds(be.data);
      setStudents(s.data.filter(st => st.boarding_type === 'interne' || !st.bed_id));
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const handleAddBuilding = async (e) => {
    e.preventDefault();
    if (!newBuilding.name) { toast.error('Nom requis'); return; }
    setFormLoading(true);
    try {
      await api.post('/buildings', newBuilding);
      toast.success('Batiment cree');
      setShowBuildingDialog(false);
      setNewBuilding({ name: '', description: '', gender: 'male', floors: 2 });
      fetchData();
    } catch (e) { toast.error('Erreur'); }
    finally { setFormLoading(false); }
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();
    if (!newRoom.name || !newRoom.building_id) { toast.error('Champs requis'); return; }
    setFormLoading(true);
    try {
      await api.post('/rooms', newRoom);
      toast.success('Chambre creee');
      setShowRoomDialog(false);
      setNewRoom({ name: '', building_id: '', floor: 0, room_type: 'dortoir', capacity: 10 });
      fetchData();
    } catch (e) { toast.error('Erreur'); }
    finally { setFormLoading(false); }
  };

  const handleAddBeds = async (e) => {
    e.preventDefault();
    if (!newBed.room_id || !newBed.bed_number) { toast.error('Champs requis'); return; }
    setFormLoading(true);
    try {
      const nums = newBed.bed_number.split('-');
      if (nums.length === 2) {
        const start = parseInt(nums[0]), end = parseInt(nums[1]);
        for (let i = start; i <= end; i++) {
          await api.post('/beds', { room_id: newBed.room_id, bed_number: 'Lit ' + i });
        }
        toast.success((end - start + 1) + ' lits crees');
      } else {
        await api.post('/beds', { room_id: newBed.room_id, bed_number: newBed.bed_number });
        toast.success('Lit cree');
      }
      setShowBedDialog(false);
      setNewBed({ room_id: '', bed_number: '' });
      fetchData();
    } catch (e) { toast.error('Erreur'); }
    finally { setFormLoading(false); }
  };

  const handleAssignBed = async (bedId, studentId) => {
    try {
      await api.post('/beds/' + bedId + '/assign?student_id=' + studentId);
      toast.success('Lit attribue');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleReleaseBed = async (bedId) => {
    try {
      await api.post('/beds/' + bedId + '/release');
      toast.success('Lit libere');
      fetchData();
    } catch (e) { toast.error('Erreur'); }
  };

  const getRoomsForBuilding = (bid) => rooms.filter(r => r.building_id === bid);
  const getBedsForRoom = (rid) => beds.filter(b => b.room_id === rid);
  const getStudentName = (sid) => { const s = students.find(st => st.id === sid); return s ? s.first_name + ' ' + s.last_name : 'Inconnu'; };
  const getUnassignedStudents = () => students.filter(s => !s.bed_id);
  const getGenderLabel = (g) => ({ male: 'Garcons', female: 'Filles', mixed: 'Mixte' }[g] || g);

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40" />)}</div>;

  return (
    <div className="space-y-6" data-testid="hebergement-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Hebergement</h1><p className="text-muted-foreground text-sm">Gestion des batiments, chambres et lits</p></div>
        <div className="flex gap-2">
          <Dialog open={showBuildingDialog} onOpenChange={setShowBuildingDialog}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full"><Plus className="w-4 h-4 mr-2" />Batiment</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau batiment</DialogTitle></DialogHeader>
              <form onSubmit={handleAddBuilding} className="space-y-4">
                <div className="space-y-2"><Label>Nom *</Label><Input value={newBuilding.name} onChange={(e) => setNewBuilding({...newBuilding, name: e.target.value})} placeholder="Batiment A" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Genre</Label><Select value={newBuilding.gender} onValueChange={(v) => setNewBuilding({...newBuilding, gender: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="male">Garcons</SelectItem><SelectItem value="female">Filles</SelectItem><SelectItem value="mixed">Mixte</SelectItem></SelectContent></Select></div>
                  <div className="space-y-2"><Label>Etages</Label><Input type="number" value={newBuilding.floors} onChange={(e) => setNewBuilding({...newBuilding, floors: parseInt(e.target.value) || 1})} /></div>
                </div>
                <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={() => setShowBuildingDialog(false)}>Annuler</Button><Button type="submit" disabled={formLoading}>{formLoading ? 'Creation...' : 'Creer'}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showRoomDialog} onOpenChange={setShowRoomDialog}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full"><Plus className="w-4 h-4 mr-2" />Chambre</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle chambre</DialogTitle></DialogHeader>
              <form onSubmit={handleAddRoom} className="space-y-4">
                <div className="space-y-2"><Label>Batiment *</Label><Select value={newRoom.building_id} onValueChange={(v) => setNewRoom({...newRoom, building_id: v})}><SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger><SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Nom *</Label><Input value={newRoom.name} onChange={(e) => setNewRoom({...newRoom, name: e.target.value})} placeholder="Chambre 101" /></div>
                  <div className="space-y-2"><Label>Etage</Label><Input type="number" value={newRoom.floor} onChange={(e) => setNewRoom({...newRoom, floor: parseInt(e.target.value) || 0})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Type</Label><Select value={newRoom.room_type} onValueChange={(v) => setNewRoom({...newRoom, room_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="dortoir">Dortoir</SelectItem><SelectItem value="chambre_double">Double</SelectItem><SelectItem value="chambre_individuelle">Individuelle</SelectItem></SelectContent></Select></div>
                  <div className="space-y-2"><Label>Capacite</Label><Input type="number" value={newRoom.capacity} onChange={(e) => setNewRoom({...newRoom, capacity: parseInt(e.target.value) || 1})} /></div>
                </div>
                <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={() => setShowRoomDialog(false)}>Annuler</Button><Button type="submit" disabled={formLoading}>{formLoading ? 'Creation...' : 'Creer'}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showBedDialog} onOpenChange={setShowBedDialog}>
            <DialogTrigger asChild><Button className="rounded-full"><Plus className="w-4 h-4 mr-2" />Lits</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Ajouter des lits</DialogTitle></DialogHeader>
              <form onSubmit={handleAddBeds} className="space-y-4">
                <div className="space-y-2"><Label>Chambre *</Label><Select value={newBed.room_id} onValueChange={(v) => setNewBed({...newBed, room_id: v})}><SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Numero(s) *</Label><Input value={newBed.bed_number} onChange={(e) => setNewBed({...newBed, bed_number: e.target.value})} placeholder="Lit 1 ou 1-10" /><p className="text-xs text-muted-foreground">Format 1-10 pour plusieurs lits</p></div>
                <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={() => setShowBedDialog(false)}>Annuler</Button><Button type="submit" disabled={formLoading}>{formLoading ? 'Creation...' : 'Creer'}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {buildings.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" /><p className="text-muted-foreground mb-4">Aucun batiment</p><Button variant="outline" className="rounded-full" onClick={() => setShowBuildingDialog(true)}><Plus className="w-4 h-4 mr-2" />Creer un batiment</Button></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {buildings.map((building) => {
            const buildingRooms = getRoomsForBuilding(building.id);
            const buildingBeds = beds.filter(b => buildingRooms.some(r => r.id === b.room_id));
            const occupiedBeds = buildingBeds.filter(b => b.is_occupied).length;
            const occupancyRate = buildingBeds.length > 0 ? Math.round((occupiedBeds / buildingBeds.length) * 100) : 0;
            return (
              <Card key={building.id} data-testid={'building-' + building.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={'w-12 h-12 rounded-lg flex items-center justify-center ' + (building.gender === 'male' ? 'bg-blue-100' : building.gender === 'female' ? 'bg-pink-100' : 'bg-purple-100')}><Building2 className={'w-6 h-6 ' + (building.gender === 'male' ? 'text-blue-600' : building.gender === 'female' ? 'text-pink-600' : 'text-purple-600')} /></div>
                      <div><CardTitle className="text-lg">{building.name}</CardTitle><div className="flex items-center gap-2 mt-1"><Badge variant="outline">{getGenderLabel(building.gender)}</Badge><Badge variant="outline">{building.floors} etage(s)</Badge></div></div>
                    </div>
                    <div className="text-right"><p className="text-2xl font-bold">{occupancyRate}%</p><p className="text-xs text-muted-foreground">{occupiedBeds}/{buildingBeds.length} lits</p></div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Progress value={occupancyRate} className="h-2 mb-4" />
                  {buildingRooms.length === 0 ? (<p className="text-sm text-muted-foreground text-center py-4">Aucune chambre</p>) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {buildingRooms.map((room) => {
                        const roomBeds = getBedsForRoom(room.id);
                        const roomOccupied = roomBeds.filter(b => b.is_occupied).length;
                        return (
                          <div key={room.id} className={'p-3 rounded-lg border cursor-pointer transition-colors hover:border-primary ' + (selectedRoom?.id === room.id ? 'border-primary bg-primary/5' : '')} onClick={() => setSelectedRoom(selectedRoom?.id === room.id ? null : room)}>
                            <div className="flex items-center justify-between mb-2"><span className="font-medium text-sm">{room.name}</span><Badge variant="outline" className="text-xs">{roomOccupied}/{roomBeds.length}</Badge></div>
                            <p className="text-xs text-muted-foreground">{room.room_type === 'dortoir' ? 'Dortoir' : room.room_type === 'chambre_double' ? 'Double' : 'Individuelle'}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {selectedRoom && buildingRooms.some(r => r.id === selectedRoom.id) && (
                    <div className="mt-4 p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-medium mb-3">{selectedRoom.name} - Lits</h4>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {getBedsForRoom(selectedRoom.id).map((bed) => (
                          <div key={bed.id} className={'p-2 rounded-lg text-center text-xs border ' + (bed.is_occupied ? 'bg-primary/10 border-primary' : 'bg-green-50 border-green-200')}>
                            <BedDouble className={'w-4 h-4 mx-auto mb-1 ' + (bed.is_occupied ? 'text-primary' : 'text-green-600')} />
                            <p className="font-medium">{bed.bed_number}</p>
                            {bed.is_occupied ? (<div><p className="truncate">{getStudentName(bed.student_id)}</p><Button size="sm" variant="ghost" className="h-6 text-xs mt-1" onClick={() => handleReleaseBed(bed.id)}>Liberer</Button></div>) : (
                              <Select onValueChange={(v) => handleAssignBed(bed.id, v)}><SelectTrigger className="h-6 text-xs mt-1"><SelectValue placeholder="Attribuer" /></SelectTrigger><SelectContent>{getUnassignedStudents().map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent></Select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default HebergementPage;
